import { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";

import { numberSeparator, roundOff, isFreeUser } from "utils";

import { usePlans } from "features/Plans/hook";
import { Tag } from "components/Tag";
import { Button } from "components/Button";
import { userDetailSelect } from "Layout/slice/selector";
import { FULL_MONTH_YEAR_FORMAT, unixToDateTime } from "utils/date";
import {
  PurchasePlanCardContainer,
  BillingBackgroundIcon as BillingBackgroundIconStyled,
} from "./PurchasePlanCard.styles";
import {
  ClockRefreshIcon,
  PercentageIcon,
  BillingBackgroundIcon,
} from "assets/icons";
import { PurchasePlanModalPlaceholder } from "./PurchasePlanCardPlaceholder";
import { ChoosePlanModal } from "features/Account/v2/ChoosePlanModal/index";

const NewPurchaseCard = () => {
  const userDetails = useSelector(userDetailSelect);
  const currentPlan = userDetails?.surmount_plan;

  const { isLoading } = usePlans();

  const [choosePlaneModel, setChoosePlaneModel] = useState(false);

  const toggleSnaptradeModal = () => {
    setChoosePlaneModel((prevState) => !prevState);
  };

  const handleChangePremium = () => {
    toggleSnaptradeModal();
  };

  // No cancel subscription actions in this simplified card

  const getCancelPlanUI = () => {
    const stripeSubscription = userDetails?.stripe_subscription;
    const price = stripeSubscription?.items?.data[0]?.price.unit_amount / 100;
    const formattedPrice = numberSeparator(roundOff(price));

    // Calculate discount percentage if applicable
    const discountPercent = currentPlan?.yearly_badge_message || null;

    // Check if it's a free plan
    const isFreePlan =
      isFreeUser() ||
      userDetails?.user_type === "FREE" ||
      currentPlan?.name?.toLowerCase().includes("free") ||
      currentPlan?.price === 0;

    return (
      <Col md={12} className="current-subscription-wrapper">
        {/* Header with Plan Name and Badge */}
        <div className="plan-name-wrapper">
          <div className="subscription-header">
            <div className="plan-title-section">
              <h2 className="plan-name">
                {currentPlan?.name || "Surmount Pro"}
              </h2>
              {!isFreePlan && (
                <Tag
                  label={
                    currentPlan?.interval?.charAt(0).toUpperCase() +
                      currentPlan?.interval?.slice(1) || "Yearly"
                  }
                />
              )}
            </div>
          </div>

          {/* Price Display */}
          <div className="price-display">
            <span className="price-amount">${formattedPrice}</span>
            <span className="price-currency">USD</span>
          </div>
        </div>

        {/* Billing Background SVG */}
        <BillingBackgroundIconStyled $isFreePlan={isFreePlan}>
          <BillingBackgroundIcon />
        </BillingBackgroundIconStyled>

        {/* Info Cards Row - Only show for paid plans */}
        {!isFreePlan && (
          <div className="info-cards-row-wrapper">
            <div className="info-cards-row">
              <div className="info-card">
                <div className="info-card-content">
                  <div className="info-icon">
                    <ClockRefreshIcon />
                  </div>
                  <div className="info-content">
                    <div className="info-label">Next billing</div>
                  </div>
                </div>
                <div className="info-value">
                  Due on{" "}
                  {unixToDateTime(
                    stripeSubscription?.current_period_end,
                    FULL_MONTH_YEAR_FORMAT
                  ) || "N/A"}
                </div>
              </div>

              {discountPercent && (
                <div className="info-card">
                  <div className="info-card-content">
                    <div className="info-icon">
                      <PercentageIcon />
                    </div>
                    <div className="info-content">
                      <div className="info-label">Discount applied</div>
                    </div>
                  </div>
                  <div className="info-value">
                    <span className="discount-badge">{discountPercent}</span>
                    <span className="discount-text">
                      {currentPlan?.interval?.charAt(0).toUpperCase() +
                        currentPlan?.interval?.slice(1) || "Yearly"}{" "}
                      plan
                    </span>
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="secondary"
              className="update-plan-btn"
              onClick={handleChangePremium}
            >
              Update
            </Button>
          </div>
        )}
        {isFreePlan && (
          <div className="free-plan-wrapper">
            <span className="free-plan-text">
              Update to <b>Surmount Core</b> for Dashboard insights, external
              account connections, and free strategies!
            </span>
            <Button
              variant="secondary"
              className="update-plan-btn w-auto"
              onClick={handleChangePremium}
            >
              Update
            </Button>
          </div>
        )}

        {/* Cancel Subscription button removed as requested */}
      </Col>
    );
  };

  return (
    <PurchasePlanCardContainer $planName={currentPlan?.name}>
      {isLoading ? (
        <PurchasePlanModalPlaceholder />
      ) : (
        <Row>
          <div className="content-wrapper">
            {/* Content can be added here if needed */}
          </div>
          {getCancelPlanUI()}
        </Row>
      )}
      {/* Cancel subscription modals removed */}
      <ChoosePlanModal show={choosePlaneModel} onHide={toggleSnaptradeModal} />
    </PurchasePlanCardContainer>
  );
};

export { NewPurchaseCard };
